import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { accessorService } from '../services/adminAuth';
import { FileText, CheckCircle, AlertCircle, Upload, ArrowLeft, Download, User, MessageSquare } from 'lucide-react';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import Swal from 'sweetalert2';

// Interface for the API response
interface ReviewerComment {
    reviewerName: string;
    score: number | null;
    isCompleted: boolean;
    remarks: string | null;
    submittedAt: string | null;
    assignededAt: string;
    reviewAssignmentId: string;
    decisions: any[];
}

interface SubmissionCaseFile {
    submissionId: string;
    submissionNumber: string;
    title: string;
    status: string;
    uploadedManuscriptUrl: string;
    applicationType: string;
    category: string;
    abstract: string;
    reviews: ReviewerComment[];
    chairmanDecision: any;
    edition?: string;
    volume?: string;
    issue?: string;
    keywords?: string;
    publicationUrl?: string;
}

const ReviewEvaluation: React.FC = () => {
    const params = useParams();
    const id = params["*"];
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const mode = searchParams.get('mode') || 'review';
    const isReadOnly = mode === 'view';

    const [caseFile, setCaseFile] = useState<SubmissionCaseFile | null>(null);
    const [score, setScore] = useState<string>('');
    const [reviewerRemark, setReviewerRemark] = useState('');
    const [recommendation, setRecommendation] = useState('Accept');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Commented out for future use - chapter sections state
    // const [sections, setSections] = useState<EvaluationSection[]>([]);
    // const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ '1': true });

    useEffect(() => {
        const loadData = async () => {
            if (!id) {
                return;
            }
            setLoading(true);
            try {
                const res = await accessorService.getSubmissionCaseFileByReviewer(id);
                if (res.isSuccess && res.data) {
                    setCaseFile(res.data);

                    // Prefill score and remarks if they exist (e.g. from a saved draft)
                    if (res.data.reviews && res.data.reviews.length > 0) {
                        const existingReview = res.data.reviews[0];
                        if (existingReview.score !== null) {
                            setScore(existingReview.score.toString());
                        }
                        if (existingReview.remarks) {
                            setReviewerRemark(existingReview.remarks);
                        }
                    }
                } else {
                    Swal.fire('Error', res.message || 'Failed to load submission data', 'error');
                    navigate('/admin/manuscript-reviews');
                }
            } catch (error) {
                Swal.fire('Error', 'An error occurred while loading the submission', 'error');
                navigate('/admin/manuscript-reviews');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, navigate]);

    const handleScoreChange = (value: string) => {
        if (isReadOnly) return;
        if (value === '') {
            setScore('');
            return;
        }
        if (!/^\d+$/.test(value)) return;
        const numValue = parseInt(value);
        if (numValue < 0 || numValue > 100) return; // Ensure score is between 0 and 100
        setScore(value);
    };

    const handleDownloadManuscript = async () => {
        if (!caseFile?.uploadedManuscriptUrl) {
            Swal.fire('Error', 'No manuscript file available for download', 'error');
            return;
        }

        setIsDownloading(true);
        try {
            const fullUrl = `${FILE_BASE_URL}/${caseFile.uploadedManuscriptUrl}`;
            const extension = caseFile.uploadedManuscriptUrl.split('.').pop();
            const filename = `Manuscript_${caseFile.submissionNumber}.${extension}`;

            await downloadFileFromUrl(fullUrl, filename);

            Swal.fire({
                icon: 'success',
                title: 'Download Started',
                text: 'Your manuscript download has begun.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to download manuscript. Please try again.', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const saveGrading = async (submit: boolean) => {
        if (isReadOnly || !caseFile) return;

        // Validation for submission
        if (submit) {
            if (!score) {
                Swal.fire('Required', 'Please enter a score before submitting.', 'warning');
                return;
            }
            if (!reviewerRemark) {
                Swal.fire('Required', 'Please enter your remarks before submitting.', 'warning');
                return;
            }
        }

        const confirmResult = await Swal.fire({
            title: submit ? 'Confirm Submission' : 'Save Draft?',
            text: submit
                ? "Are you sure you want to submit this review? This action cannot be undone."
                : "Your progress will be saved. You can continue later.",
            icon: submit ? 'warning' : 'info',
            showCancelButton: true,
            confirmButtonColor: '#088A61',
            cancelButtonColor: '#d33',
            confirmButtonText: submit ? 'Yes, Submit Review' : 'Yes, Save Draft'
        });

        if (confirmResult.isConfirmed) {
            setSubmitting(true);
            try {
                if (!caseFile.reviews || caseFile.reviews.length === 0) {
                    throw new Error("No review assignment found");
                }
                const reviewAssignmentId = caseFile.reviews[0].reviewAssignmentId;

                const payload = {
                    reviewAssignmentId: reviewAssignmentId,
                    submissionId: caseFile.submissionId,
                    score: score ? parseInt(score) : 0,
                    remarks: reviewerRemark,
                    isSubmitted: submit
                };

                const res = await accessorService.submitGrading(payload);

                if (res.isSuccess) {
                    await Swal.fire(
                        submit ? 'Submitted!' : 'Saved!',
                        submit ? 'Review has been submitted successfully.' : 'Draft has been saved successfully.',
                        'success'
                    );
                    navigate('/admin/manuscript-reviews');
                } else {
                    Swal.fire('Error', res.message || 'Action failed', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'An error occurred during submission', 'error');
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleSubmit = () => saveGrading(true);
    const handleSaveDraft = () => saveGrading(false);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="w-10 h-10 border-4 border-gray-100 border-t-[#088A61] rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-7xl mx-auto px-6 py-6 pb-20">
            <div className="mb-6 flex gap-4 items-center">
                <Link to="/admin/manuscript-reviews" className="inline-flex items-center gap-2 text-sm font-500">
                    <ArrowLeft className="w-5 h-5" />
                    Back
                </Link>
                <div className="flex-1">
                    <h1 className="text-2xl font-black text-gray-900">Manuscript Review</h1>
                    <p className="text-sm text-gray-500">Submission ID: {caseFile?.submissionNumber}</p>
                </div>
                {/* Download Manuscript Button */}
                <button
                    onClick={handleDownloadManuscript}
                    disabled={isDownloading || !caseFile?.uploadedManuscriptUrl}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#088A61] text-white rounded-lg text-sm font-semibold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isDownloading ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            Downloading...
                        </>
                    ) : (
                        <>
                            <Download className="w-4 h-4" />
                            Download Manuscript
                        </>
                    )}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                <div className="lg:col-span-7 space-y-6">

                    {/* Submission Details */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 leading-tight">{caseFile?.title}</h2>

                        <div className="grid grid-cols-2 gap-6 mb-6">
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Submission ID</p>
                                <p className="text-sm font-bold text-gray-900">{caseFile?.submissionNumber}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Thematic Area</p>
                                <p className="text-sm font-bold text-gray-900">{caseFile?.category || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Application Type</p>
                                <p className="text-sm font-bold text-gray-900">{caseFile?.applicationType || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Status</p>
                                <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${caseFile?.status === 'UnderReview' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'
                                    }`}>
                                    {caseFile?.status === 'UnderReview' ? 'Under Review' : caseFile?.status}
                                </span>
                            </div>
                        </div>

                        {/* New Fields */}
                        <div className="pt-6 border-t border-gray-50 grid grid-cols-2 gap-6">
                            {/* Keywords removed from display */}
                            {caseFile?.applicationType?.toLowerCase().includes('book') && caseFile?.edition && (
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Edition</p>
                                    <p className="text-sm font-bold text-gray-900">{caseFile.edition}</p>
                                </div>
                            )}
                            {caseFile?.applicationType?.toLowerCase().includes('journal') && (
                                <>
                                    {caseFile?.volume && (
                                        <div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Volume</p>
                                            <p className="text-sm font-bold text-gray-900">{caseFile.volume}</p>
                                        </div>
                                    )}
                                    {caseFile?.issue && (
                                        <div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Issue</p>
                                            <p className="text-sm font-bold text-gray-900">{caseFile.issue}</p>
                                        </div>
                                    )}
                                    {caseFile?.publicationUrl && (
                                        <div className="col-span-full">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Publication URL</p>
                                            <a href={caseFile.publicationUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm font-bold break-all block">
                                                {caseFile.publicationUrl}
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Abstract */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-gray-900 mb-3">Abstract</h3>
                        <p className="text-sm text-gray-600 leading-relaxed italic">
                            {caseFile?.abstract}
                        </p>
                    </div>

                    {/* Reviewers Comments Section */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-gray-500" />
                            Reviewers
                        </h3>
                        <div className="space-y-4">
                            {caseFile?.reviews && caseFile.reviews.length > 0 ? (
                                caseFile.reviews.map((review, index) => (
                                    <div key={index} className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-[#088A61]/10 rounded-full flex items-center justify-center">
                                                    <User className="w-5 h-5 text-[#088A61]" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{review.reviewerName?.trim()}</p>
                                                    <p className="text-xs text-gray-500">Assigned: {formatDate(review.assignededAt)}</p>
                                                </div>
                                            </div>
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${review.isCompleted
                                                ? 'bg-green-50 text-green-700'
                                                : 'bg-amber-50 text-amber-700'
                                                }`}>
                                                {review.isCompleted ? 'Completed' : 'Pending'}
                                            </span>
                                        </div>

                                        {review.isCompleted && (
                                            <div className="mt-3 space-y-2">
                                                {review.score !== null && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-gray-500">Score:</span>
                                                        <span className="text-sm font-bold text-[#088A61]">{review.score}</span>
                                                    </div>
                                                )}
                                                {review.remarks && (
                                                    <div>
                                                        <span className="text-xs font-medium text-gray-500">Remarks:</span>
                                                        <p className="text-sm text-gray-700 mt-1 bg-white p-3 rounded-lg border border-gray-100">
                                                            {review.remarks}
                                                        </p>
                                                    </div>
                                                )}
                                                {review.submittedAt && (
                                                    <p className="text-xs text-gray-400">
                                                        Submitted: {formatDate(review.submittedAt)}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">No reviewers assigned yet</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 
                    ==========================================
                    CHAPTER STRUCTURE - COMMENTED OUT FOR FUTURE USE
                    ==========================================
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Chapter Structure</h3>
                        <div className="space-y-3">
                            {sections.map((section, index) => (
                                <div key={section.id} className="border border-gray-100 rounded-xl overflow-hidden">
                                    <button
                                        onClick={() => toggleSection(section.id)}
                                        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm font-semibold text-gray-900">Chapter {index + 1}: {section.name}</span>
                                        </div>
                                        {expandedSections[section.id] ? (
                                            <ChevronUp className="w-4 h-4 text-gray-400" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        )}
                                    </button>

                                    {expandedSections[section.id] && (
                                        <div className="px-4 pb-4 pt-2 bg-gray-50/50 border-t border-gray-100">
                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                {section.content}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    */}
                </div>

                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">

                        <h2 className="text-lg font-bold text-gray-900">Review Evaluation</h2>

                        {/* Single Score Input */}
                        <div>
                            <h4 className="text-sm font-bold text-gray-900 mb-3">Review Score</h4>
                            <div className="flex items-center gap-3">
                                <input
                                    type="text"
                                    value={score}
                                    onChange={(e) => handleScoreChange(e.target.value)}
                                    disabled={isReadOnly}
                                    placeholder="0"
                                    className={`w-24 text-center py-3 px-4 rounded-lg border text-lg font-bold ${isReadOnly
                                        ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-white border-gray-300 text-gray-900 focus:border-[#088A61] focus:ring-2 focus:ring-[#088A61]/20 outline-none'
                                        }`}
                                />
                                <span className="text-sm text-gray-400 font-medium">/ 100</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Enter your overall score for this manuscript (0-100)</p>
                        </div>

                        {/* 
                        ==========================================
                        CHAPTER SCORES - COMMENTED OUT FOR FUTURE USE
                        ==========================================
                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-gray-900">Chapter Scores</h4>
                            {sections.map((section, idx) => (
                                <div key={section.id} className="space-y-2">
                                    <label className="text-xs font-medium text-gray-600">
                                        Chapter {idx + 1}: {section.name}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={section.score === 0 ? '' : section.score}
                                            onChange={(e) => handleScoreChange(section.id, e.target.value)}
                                            disabled={isReadOnly}
                                            placeholder="0"
                                            className={`w-20 text-center py-2 px-3 rounded-lg border text-sm font-bold ${isReadOnly
                                                ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-white border-gray-300 text-gray-900 focus:border-[#088A61] focus:ring-2 focus:ring-[#088A61]/20 outline-none'
                                            }`}
                                        />
                                        <span className="text-sm text-gray-400">/ {section.maxScore}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-xl">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-gray-900">Final Score</span>
                                <span className="text-2xl font-black text-[#088A61]">
                                    {totals.currentTotal}.0 <span className="text-gray-400 text-base">/ {totals.maxTotal}</span>
                                </span>
                            </div>
                        </div>
                        */}

                        {/* Reviewer Remarks */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-bold text-gray-900">Reviewer Remarks</label>
                                <span className={`text-xs font-medium ${reviewerRemark.length >= 200 ? 'text-red-500' : 'text-gray-400'}`}>
                                    {reviewerRemark.length}/200
                                </span>
                            </div>
                            <textarea
                                value={reviewerRemark}
                                onChange={(e) => setReviewerRemark(e.target.value.slice(0, 200))}
                                disabled={isReadOnly}
                                placeholder="Enter specific comments or observations for the author..."
                                rows={4}
                                className={`w-full p-3 rounded-lg border text-sm resize-none outline-none ${isReadOnly
                                    ? 'bg-gray-50 border-gray-200 text-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 focus:border-[#088A61] focus:ring-2 focus:ring-[#088A61]/20'
                                    }`}
                            />
                        </div>

                        {/* Final Recommendation */}
                        {/* <div>
                            <h4 className="text-lg font-bold text-gray-900 mb-2">Final Recommendation</h4>
                            <p className="text-sm text-gray-500 mb-4">Select your overall recommendation</p>
                            <div className="space-y-3">
                                {[
                                    { id: 'Accept', label: 'Acceptable for Publication', description: 'Manuscript meets all standards', textColor: 'text-green-600', borderColor: 'border-green-500', bgColor: 'bg-green-50' },
                                    { id: 'Minor Revision', label: 'Minor Revision Required', description: 'Small improvements needed', textColor: 'text-blue-600', borderColor: 'border-blue-500', bgColor: 'bg-blue-50' },
                                    { id: 'Major Revision', label: 'Major Revision Required', description: 'Significant changes needed', textColor: 'text-orange-600', borderColor: 'border-orange-500', bgColor: 'bg-orange-50' },
                                    { id: 'Reject', label: 'Not Acceptable for Publication', description: 'Does not meet standards', textColor: 'text-red-600', borderColor: 'border-red-500', bgColor: 'bg-red-50' }
                                ].map((opt) => (
                                    <label
                                        key={opt.id}
                                        className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${recommendation === opt.id
                                            ? `${opt.bgColor} ${opt.borderColor}`
                                            : 'bg-white border-gray-200 hover:border-gray-300'
                                            } ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}
                                    >
                                        <div className="flex-1">
                                            <p className={`font-bold text-sm mb-1 ${recommendation === opt.id ? opt.textColor : 'text-gray-900'}`}>
                                                {opt.label}
                                            </p>
                                            <p className="text-xs text-gray-500">{opt.description}</p>
                                        </div>
                                        <input
                                            type="radio"
                                            name="recommendation"
                                            value={opt.id}
                                            checked={recommendation === opt.id}
                                            onChange={(e) => setRecommendation(e.target.value)}
                                            disabled={isReadOnly}
                                            className={`w-5 h-5 flex-shrink-0 ${recommendation === opt.id ? 'accent-current' : 'accent-gray-300'}`}
                                        />
                                    </label>
                                ))}
                            </div>
                        </div> */}

                        {/* Attachments */}
                        {/* <div>
                            <h4 className="text-sm font-bold text-gray-900 mb-3">Attachments</h4>
                            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${isReadOnly ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300 hover:border-[#088A61] cursor-pointer'
                                }`}>
                                <Upload className={`w-6 h-6 mx-auto mb-2 ${isReadOnly ? 'text-gray-300' : 'text-gray-400'}`} />
                                <p className="text-xs text-gray-500">Attach File</p>
                            </div>
                        </div> */}

                        {/* Action Buttons */}
                        {!isReadOnly && (
                            <div className="space-y-3 pt-4">
                                <button
                                    onClick={handleSaveDraft}
                                    disabled={submitting}
                                    className="w-full py-3 rounded-lg border-2 border-gray-900 text-gray-900 font-bold text-sm hover:bg-gray-50 transition-all disabled:opacity-50"
                                >
                                    {submitting ? 'Saving...' : 'Save Draft'}
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className="w-full py-3 rounded-lg bg-[#088A61] text-white font-bold text-sm shadow-lg hover:bg-[#067451] transition-all disabled:opacity-70"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin inline-block mr-2"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-4 h-4 inline mr-2" />
                                            Submit Review
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {isReadOnly && (
                            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-blue-700 leading-relaxed">
                                    This review has been submitted and is now in view mode.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewEvaluation;
